import React, { useCallback, useEffect } from 'react';
import { Modal } from '@spotify-internal/anchor-ds';
import { ButtonPrimary, ButtonSecondary } from '@spotify-internal/encore-web';
import AnchorAPI from 'modules/AnchorAPI';
import { useT } from 'i18n/hooks/useT';
import { useParams } from 'react-router-dom';
import {
  trackVerifyEmailModalButtonClick,
  trackVerifyEmailModalClose,
  trackVerifyEmailModalViewed,
} from 'client/screens/VerifyUserEmailScreen/events';

type Props = {
  type: 'verify' | 'pending' | 'publish';
  onResendEmail?: () => void;
  onClickClose(): void;
};

const EmailVerificationModal = ({
  onClickClose,
  onResendEmail,
  type,
}: Props) => {
  const [pendingEmail, setPendingEmail] = React.useState('');
  const t = useT();
  const { integrationName } = useParams<{ integrationName: string }>();
  const isRiversideUser = integrationName === 'riverside';

  useEffect(() => {
    if (isRiversideUser) {
      trackVerifyEmailModalViewed();
    }
    if (type === 'pending') {
      AnchorAPI.fetchPendingEmail().then(({ email }: { email: string }) => {
        setPendingEmail(email);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConfirmationButtonClick = useCallback(() => {
    if (isRiversideUser) {
      trackVerifyEmailModalButtonClick();
    }
    onClickClose();
  }, [onClickClose, isRiversideUser]);

  const onModalClose = useCallback(() => {
    if (isRiversideUser) {
      trackVerifyEmailModalClose();
    }
    onClickClose();
  }, [onClickClose]);

  function content() {
    switch (type) {
      case 'verify':
        return (
          <span>
            {t(
              'SettingsScreen.EmailVerificationModal.VerifyEmailMessage',
              'Click the link in the email we just sent you to verify your new email address. Your account information will not be updated until you have verified this change.',
              'Verify email address modal message'
            )}
          </span>
        );
      case 'pending':
        return (
          <span>
            {t(
              'SettingsScreen.EmailVerificationModal.EmailPendingMessage',
              "Please click the link in the email we sent to {pendingEmail} to verify your new email address. Your account information will be updated once you've verified this change.",
              'Verify email address modal message',
              { pendingEmail }
            )}
          </span>
        );
      case 'publish':
        return (
          <span>
            {t(
              'SettingsScreen.EmailVerificationModal.BeforePublishMessage',
              "Before we can publish this episode, you'll need to verify your email address. Just click the link in the email we sent you!",
              'Verify email address modal message'
            )}
          </span>
        );
      default:
        return <span>no type specified</span>;
    }
  }
  return (
    <Modal
      isShowing
      isShowingCloseButton
      aria-labelledby="email-verification-modal-title"
      onClose={onModalClose}
    >
      <Modal.Title id="email-verification-modal-title">
        {t(
          'SettingsScreen.EmailVerificationModal.Title',
          'Verify your email address',
          'Verify email address modal title'
        )}
      </Modal.Title>
      <Modal.Body>{content()}</Modal.Body>
      <Modal.Footer className="buttonContainer">
        {onResendEmail && (
          <ButtonSecondary onClick={onResendEmail}>
            {t(
              'Button.ResendEmail',
              'Resend email',
              'Resend email button text'
            )}
          </ButtonSecondary>
        )}
        <ButtonPrimary onClick={onConfirmationButtonClick}>
          {t(
            'Button.GotItThanks',
            'Got it, thanks',
            'Confirmation button text'
          )}
        </ButtonPrimary>
      </Modal.Footer>
    </Modal>
  );
};

export { EmailVerificationModal };
